import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Save, ArrowLeft, RefreshCw, Users, Upload, Image as ImageIcon, Loader2, Link as LinkIcon, Share2, Check } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, authApi, authHeaders, formatError } from "../../lib/adminApi";
import { formatSlotIst, generateRecurringSlots } from "../../lib/calendar";
import { buildShareUrl, buildWhatsappHref } from "../../lib/trainingShare";

const LANGS = ["en", "hi", "mr"];
const DEVICES = ["desktop", "mobile"];
const WEEKDAYS = [
  { i: 0, l: "M", full: "Mon" },
  { i: 1, l: "T", full: "Tue" },
  { i: 2, l: "W", full: "Wed" },
  { i: 3, l: "T", full: "Thu" },
  { i: 4, l: "F", full: "Fri" },
  { i: 5, l: "S", full: "Sat" },
  { i: 6, l: "S", full: "Sun" },
];

function AdminCopyLink({ session }) {
  const [copied, setCopied] = useState(false);
  const url = buildShareUrl(session);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* ignore */
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={`training-copy-link-${session.id}`}
      title={copied ? "Copied!" : "Copy shareable link"}
      className="p-2 rounded-lg text-slate-500 hover:text-[#2A5CB8] hover:bg-white"
    >
      {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
    </button>
  );
}

export default function AdminTrainings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("sessions"); // sessions | bookings
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/trainings?only_active=false`);
      setSessions(data);
    } catch (e) {
      setError(formatError(e));
    }
  };
  const loadBookings = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/trainings/bookings`, { headers: authHeaders() });
      setBookings(data);
    } catch (e) {
      if (e?.response?.status === 401) { authApi.logout(); navigate("/admin"); return; }
      setError(formatError(e));
    }
  };
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { if (tab === "bookings") loadBookings(); }, [tab]);

  const save = async (payload, id) => {
    setBusy(true); setError(null);
    try {
      if (id) await axios.put(`${API}/admin/trainings/${id}`, payload, { headers: authHeaders() });
      else await axios.post(`${API}/admin/trainings`, payload, { headers: authHeaders() });
      setEditing(null); await loadSessions();
    } catch (e) {
      if (e?.response?.status === 401) { authApi.logout(); navigate("/admin"); return; }
      setError(formatError(e));
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this training session?")) return;
    try {
      await axios.delete(`${API}/admin/trainings/${id}`, { headers: authHeaders() });
      await loadSessions();
    } catch (e) { setError(formatError(e)); }
  };

  return (
    <div className="min-h-[100dvh]">
      <div className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/40">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/dashboard")} className="p-2 rounded-full text-slate-500 hover:bg-white/60" aria-label="Back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Logo className="h-7" />
            <span className="hidden sm:inline text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Trainings admin</span>
          </div>
          <button onClick={loadSessions} className="p-2 rounded-full text-slate-500 hover:bg-white/60" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 pt-24 pb-12">
        <div className="inline-flex bg-white/70 backdrop-blur-md rounded-full p-1 border border-white/80 mb-6">
          <TabBtn active={tab === "sessions"} onClick={() => setTab("sessions")} testId="training-tab-sessions">Sessions</TabBtn>
          <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testId="training-tab-bookings">
            <Users className="w-4 h-4 inline mr-1" /> Bookings
          </TabBtn>
        </div>

        {error && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2.5">{error}</div>}

        {tab === "sessions" && (
          <>
            <div className="mb-4 flex justify-end">
              <button onClick={() => setEditing({ isNew: true })} data-testid="training-add-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2 text-sm font-semibold">
                <Plus className="w-4 h-4" /> Add session
              </button>
            </div>
            <div className="glass-strong rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/60 sticky top-0 backdrop-blur-md">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3 w-32">Frequency</th>
                    <th className="px-4 py-3 w-20">Slots</th>
                    <th className="px-4 py-3 w-24">Variants</th>
                    <th className="px-4 py-3 w-20">Active</th>
                    <th className="px-4 py-3 w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="border-t border-white/60" data-testid={`training-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0F172A]">{s.title}</div>
                        {s.category && <div className="text-xs text-[#2A5CB8]">{s.category}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{s.frequency_label || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{(s.slots || []).length}</td>
                      <td className="px-4 py-3 text-slate-700">{(s.variants || []).length}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                          {s.active ? "Live" : "Off"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1 items-center">
                          <AdminCopyLink session={s} />
                          <a
                            href={buildWhatsappHref(s)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`training-share-${s.id}`}
                            title="Share on WhatsApp"
                            className="p-2 rounded-lg text-emerald-600 hover:text-white hover:bg-[#25D366]"
                          >
                            <Share2 className="w-4 h-4" />
                          </a>
                          <button onClick={() => setEditing(s)} data-testid={`training-edit-${s.id}`} className="p-2 rounded-lg text-slate-500 hover:text-[#FF5E1A] hover:bg-white">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(s.id)} data-testid={`training-delete-${s.id}`} className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-white">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No sessions yet. Add your first one.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "bookings" && (
          <div className="glass-strong rounded-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/60 sticky top-0 backdrop-blur-md">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">Slot</th>
                  <th className="px-4 py-3 w-20">Lang</th>
                  <th className="px-4 py-3 w-24">Device</th>
                  <th className="px-4 py-3 w-24">Mode</th>
                  <th className="px-4 py-3 w-24">Joined</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => {
                  const s = sessions.find((x) => x.id === b.session_id);
                  return (
                    <tr key={b.id} className="border-t border-white/60">
                      <td className="px-4 py-3 font-semibold">{b.name}</td>
                      <td className="px-4 py-3 text-slate-600">{b.phone}</td>
                      <td className="px-4 py-3">{s?.title || b.session_id}</td>
                      <td className="px-4 py-3 text-slate-600">{formatSlotIst(b.slot)}</td>
                      <td className="px-4 py-3 uppercase font-semibold">{b.language}</td>
                      <td className="px-4 py-3">{b.device}</td>
                      <td className="px-4 py-3 text-xs">{b.mode}</td>
                      <td className="px-4 py-3">
                        {b.joined_at ? <span className="text-emerald-600 font-semibold">✓</span> : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
                {bookings.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No bookings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EditModal
          initial={editing.isNew ? null : editing}
          onSave={save}
          onClose={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${active ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"}`}>
    {children}
  </button>
);

const EditModal = ({ initial, onSave, onClose, busy }) => {
  const [form, setForm] = useState(
    initial || {
      title: "", category: "", description: "", image_url: "",
      frequency_label: "Conducted 3 times a week",
      duration_minutes: 60, min_attendees_floor: 50,
      waiting_caption: "Host is letting you in…",
      variants: [], slots: [], schedule_days: [], schedule_times: [], active: true,
    },
  );

  // Recurring schedule helpers
  const scheduleDays = form.schedule_days || [];
  const scheduleTimes = form.schedule_times || [];
  const toggleDay = (i) => {
    const next = scheduleDays.includes(i)
      ? scheduleDays.filter((d) => d !== i)
      : [...scheduleDays, i].sort((a, b) => a - b);
    setForm({ ...form, schedule_days: next });
  };
  const addTime = () => setForm({ ...form, schedule_times: [...scheduleTimes, "11:00"] });
  const setTime = (idx, val) =>
    setForm({ ...form, schedule_times: scheduleTimes.map((t, i) => (i === idx ? val : t)) });
  const removeTime = (idx) =>
    setForm({ ...form, schedule_times: scheduleTimes.filter((_, i) => i !== idx) });

  const previewSlots = generateRecurringSlots(
    scheduleDays,
    scheduleTimes,
    Number(form.duration_minutes) || 60,
    6,
  );

  // Slot input helper - one line per ISO date-time in IST
  const toggleVariant = (lang, device) => {
    const key = `${lang}:${device}`;
    const found = (form.variants || []).find((v) => v.lang === lang && v.device === device);
    if (found) {
      setForm({ ...form, variants: form.variants.filter((v) => !(v.lang === lang && v.device === device)) });
    } else {
      setForm({ ...form, variants: [...(form.variants || []), { lang, device, supademo_url: "" }] });
    }
  };

  const setVariantUrl = (lang, device, url) => {
    setForm({
      ...form,
      variants: (form.variants || []).map((v) =>
        v.lang === lang && v.device === device ? { ...v, supademo_url: url } : v,
      ),
    });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const hasSchedule = scheduleDays.length > 0 && scheduleTimes.filter(Boolean).length > 0;
    const payload = {
      ...form,
      title: form.title.trim(),
      duration_minutes: Number(form.duration_minutes) || 60,
      min_attendees_floor: Number(form.min_attendees_floor) || 50,
      schedule_days: scheduleDays,
      schedule_times: scheduleTimes.filter(Boolean),
      // Recurring sessions generate slots server-side; keep manual slots only when no schedule.
      slots: hasSchedule ? [] : (form.slots || []),
      // strip empty-url variants
      variants: (form.variants || []).filter((v) => v.supademo_url.trim()),
    };
    onSave(payload, initial?.id || null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-2xl glass-strong rounded-3xl p-6 md:p-7 flex flex-col gap-4 max-h-[92dvh] overflow-y-auto"
        data-testid="training-edit-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold text-[#0F172A]">{initial ? "Edit session" : "New session"}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-full text-slate-500 hover:bg-white/70"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Title" required>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm" data-testid="training-form-title" required />
          </Field>
          <Field label="Category">
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm" />
          </Field>
        </div>
        <Field label="Description">
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm min-h-[70px]" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Image">
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
            />
          </Field>
          <Field label='Frequency label (e.g. "Conducted 3 times a week")'>
            <input value={form.frequency_label} onChange={(e) => setForm({ ...form, frequency_label: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Duration (min)">
            <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm" />
          </Field>
          <Field label="Min attendees (floor)">
            <input type="number" value={form.min_attendees_floor} onChange={(e) => setForm({ ...form, min_attendees_floor: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm" />
          </Field>
          <Field label="Waiting caption">
            <input value={form.waiting_caption} onChange={(e) => setForm({ ...form, waiting_caption: e.target.value })} className="w-full px-3 py-2 rounded-xl glass-input text-sm" />
          </Field>
        </div>

        <Field label="Schedule — jab training conduct hoti hai">
          <div className="rounded-2xl border border-slate-200 bg-white/50 p-4 flex flex-col gap-4">
            {/* Days of week */}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Days</span>
              <div className="flex gap-1.5 flex-wrap">
                {WEEKDAYS.map((d, idx) => {
                  const on = scheduleDays.includes(d.i);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleDay(d.i)}
                      data-testid={`schedule-day-${d.i}`}
                      title={d.full}
                      className={`w-10 h-10 rounded-full text-sm font-bold transition-all ${
                        on
                          ? "bg-[#FF5E1A] text-white shadow-sm"
                          : "bg-white border border-slate-300 text-slate-600 hover:border-[#FF5E1A]"
                      }`}
                    >
                      {d.l}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Timings */}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Timings (IST)</span>
              <div className="flex flex-col gap-2">
                {scheduleTimes.length === 0 && (
                  <p className="text-xs text-slate-400">No timings yet. Add one below.</p>
                )}
                {scheduleTimes.map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="time"
                      value={t}
                      onChange={(e) => setTime(idx, e.target.value)}
                      data-testid={`schedule-time-${idx}`}
                      className="px-3 py-2 rounded-xl glass-input text-sm w-36"
                    />
                    <button
                      type="button"
                      onClick={() => removeTime(idx)}
                      data-testid={`schedule-time-remove-${idx}`}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-white"
                      aria-label="Remove timing"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addTime}
                data-testid="schedule-add-time"
                className="inline-flex items-center gap-1.5 self-start rounded-full bg-white border border-slate-300 hover:border-[#FF5E1A] px-3 py-1.5 text-xs font-semibold text-[#0F172A]"
              >
                <Plus className="w-3.5 h-3.5" /> Add timing
              </button>
            </div>

            {/* Preview */}
            {previewSlots.length > 0 ? (
              <div className="rounded-xl bg-[#F0FAF3] border border-emerald-100 p-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1.5">
                  Next sessions (auto-generated)
                </p>
                <div className="flex flex-col gap-0.5" data-testid="schedule-preview">
                  {previewSlots.map((s) => (
                    <span key={s} className="text-xs text-[#0F172A]">{formatSlotIst(s)}</span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400">
                Select at least one day and one timing to see upcoming sessions.
              </p>
            )}
          </div>
        </Field>

        <Field label="Supademo variants (check the boxes you have URLs for)">
          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-white/60">
                <tr className="text-left text-slate-500 font-semibold">
                  <th className="p-2 w-20">Lang \ Device</th>
                  {DEVICES.map((d) => <th key={d} className="p-2">{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {LANGS.map((lang) => (
                  <tr key={lang} className="border-t border-slate-100">
                    <td className="p-2 font-bold uppercase text-slate-700">{lang}</td>
                    {DEVICES.map((device) => {
                      const v = (form.variants || []).find((x) => x.lang === lang && x.device === device);
                      return (
                        <td key={device} className="p-2 align-top">
                          <div className="flex flex-col gap-1">
                            <label className="inline-flex items-center gap-1.5 text-slate-600 cursor-pointer">
                              <input type="checkbox" checked={!!v} onChange={() => toggleVariant(lang, device)} data-testid={`variant-${lang}-${device}`} className="accent-[#FF5E1A]" />
                              enabled
                            </label>
                            {v && (
                              <input type="url" placeholder="https://supademo.com/…" value={v.supademo_url}
                                onChange={(e) => setVariantUrl(lang, device, e.target.value)}
                                data-testid={`variant-url-${lang}-${device}`}
                                className="px-2 py-1 rounded-md border border-slate-200 text-xs" />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Field>

        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
          <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#FF5E1A]" />
          Active (show on public trainings page)
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:bg-white/70">Cancel</button>
          <button type="submit" disabled={busy} data-testid="training-form-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-semibold text-slate-600">{label}{required && " *"}</span>
    {children}
  </label>
);

const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const ImageUploader = ({ value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = React.useRef(null);

  const resolveSrc = (v) => {
    if (!v) return null;
    if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:")) return v;
    if (v.startsWith("/api/")) return `${BACKEND_ORIGIN}${v}`;
    return v;
  };

  const upload = async (file) => {
    setErr(null);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErr("File too large (max 5 MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const src = resolveSrc(value);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
          {src ? (
            <img src={src} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-6 h-6 text-slate-400" />
          )}
        </div>
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="training-image-file"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-[#FF5E1A] px-3 py-1.5 text-xs font-semibold text-[#0F172A] disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {value ? "Replace image" : "Upload image"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="ml-2 text-xs text-red-500 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <p className="text-[10px] text-slate-400">PNG/JPG/WebP, up to 5 MB.</p>
    </div>
  );
};
